import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NavElement from '../../sharedComponents/NavElement/NavElement';
import { useAuth0 } from '@auth0/auth0-react';
import Typography from '../../sharedComponents/Typography/Typography';
import { PayoutAccountAPI } from '../../api';
import { PageProps } from '../Page.types';
import { useActions, useStore } from '../../store/hooks';
import ManagePayoutAccountList from '../../containers/ManagePayoutAccountList/ManagePayoutAccountList';
import styles from './payout-account-mgmt.module.scss';
import { AddPayoutAccountContainer } from '../../containers';
import Footer from '../../components/Footer/Footer';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PayoutProvider,
  stringToPayoutProvider,
} from '../../utils/types/Entity';

type OAuthResult = 'canceled' | 'connected' | 'failed';

const oauthMessageByProvider: Partial<
  Record<PayoutProvider, Record<OAuthResult, string>>
> = {
  [PayoutProvider.PayPal]: {
    canceled: 'error.paypal_login_canceled',
    connected: 'sunshine.content.paypal_account_connected',
    failed: 'error.paypal_login_failed',
  },
};

const getOAuthProvider = (search: string): PayoutProvider | null => {
  const params = new URLSearchParams(search);
  const providerParam = params.get('oauth_provider');
  if (!providerParam) return null;
  const provider = stringToPayoutProvider(providerParam);
  if (!provider || !oauthMessageByProvider[provider]) return null;
  return provider;
};

const getOAuthResult = (search: string): OAuthResult | null => {
  const params = new URLSearchParams(search);
  const result = params.get('oauth_result');
  return result === 'canceled' || result === 'connected' || result === 'failed'
    ? result
    : null;
};

const PayoutAccountMgmt: FC<PageProps> = ({
  layout: Layout,
  navigation: Navigation,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [oauthProvider] = useState<PayoutProvider | null>(() =>
    getOAuthProvider(location.search),
  );
  const [oauthResult] = useState<OAuthResult | null>(() =>
    getOAuthResult(location.search),
  );

  const { getAccessTokenSilently } = useAuth0();
  const [hideAccountList, setHideAccountList] = useState<boolean>(false);
  const dispatch = useActions();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('oauth_provider') && !params.has('oauth_result')) return;

    params.delete('oauth_provider');
    params.delete('oauth_result');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  const {
    payoutAccount: { payoutAccounts, loadedPayoutAccounts },
    user: { user },
  } = useStore();

  useEffect(() => {
    const fetchPayoutAccounts = async (): Promise<void> => {
      const accessToken = await getAccessTokenSilently();
      try {
        // eslint-disable-next-line prettier/prettier
        const fetchedAccounts = await PayoutAccountAPI.getPayoutAccounts(
          accessToken,
          user.langLocale,
        );
        dispatch.setPayoutAccounts(fetchedAccounts);
      } catch (e) {
        //TODO: #1835 fix the 'can not perform state update on the unmounted component' error
      } finally {
        dispatch.setLoadedPayoutAccounts();
      }
    };
    if (!loadedPayoutAccounts) {
      void fetchPayoutAccounts();
    }
  }, [
    payoutAccounts,
    loadedPayoutAccounts,
    dispatch,
    getAccessTokenSilently,
    user.langLocale,
  ]);

  let renderedPayoutAccounts: JSX.Element;
  const oauthMessage =
    oauthProvider && oauthResult
      ? t(oauthMessageByProvider[oauthProvider]?.[oauthResult] ?? '')
      : '';
  const isCanceledOAuthLogin = oauthResult === 'canceled';

  if (payoutAccounts.length === 0) {
    renderedPayoutAccounts = (
      <>
        <Typography variant="body-1">
          {t('sunshine.content.payout_accounts.no_payout_accounts')}
        </Typography>
      </>
    );
  } else {
    renderedPayoutAccounts = (
      <>
        <Typography variant="body-1">
          {t('sunshine.content.payout_accounts.explainer')}
        </Typography>
        <div className={styles.spacingWrapper}>
          <ManagePayoutAccountList accounts={payoutAccounts} />
        </div>
      </>
    );
  }

  return (
    <Layout navigation={<Navigation />} footer={<Footer />}>
      <NavElement
        title={t('action.back')}
        navigateTo={-1}
        colorVariant="ocean"
      />
      <div className={styles.heading}>
        <Typography variant="h4">
          {t('sunshine.content.payout_accounts.header')}
        </Typography>
        {oauthMessage && (
          <div
            className={
              isCanceledOAuthLogin ? styles.canceledOAuthNotice : undefined
            }
            role={isCanceledOAuthLogin ? 'alert' : 'status'}
            aria-live={isCanceledOAuthLogin ? 'assertive' : 'polite'}
          >
            <Typography
              variant={
                oauthResult === 'failed' || isCanceledOAuthLogin
                  ? 'error'
                  : 'body-2'
              }
              color={oauthResult === 'connected' ? 'status green' : undefined}
            >
              {oauthMessage}
            </Typography>
          </div>
        )}
        {!hideAccountList ? (
          loadedPayoutAccounts && renderedPayoutAccounts
        ) : (
          <></>
        )}
        <div className={styles.spacingWrapper}>
          <AddPayoutAccountContainer
            processingRequest={false}
            onHideAccountList={setHideAccountList}
          />
        </div>
      </div>
    </Layout>
  );
};

export default PayoutAccountMgmt;
